import {useCallback} from 'react';
import useUpdatePayinSessionMutation from 'src/api/graphql/useUpdatePayinSessionMutation';
import {getStore} from 'src/v2/state/store';
import type {OpenapiPostV1CryptoInternalPayinSessionUpdateInputRefundParamsRefundDataDestinationNetworkEnum as DestinationNetworkEnum} from '@stripe-internal/data-v1/schema/types';

type UpdateRefundAddressParams = {
  refundAddress: string | null;
  destinationNetwork: DestinationNetworkEnum;
};

export const useUpdateRefundAddress = (): [
  (params: UpdateRefundAddressParams) => Promise<void>,
  {loading: boolean},
] => {
  const store = getStore();
  const [updatePayinSessionMutation, {loading}] =
    useUpdatePayinSessionMutation();
  const sessionId = store.useSelector((state) => state.id);
  const clientSecret = store.useSelector((state) => state.clientSecret);

  const updateRefundAddress = useCallback(
    async ({refundAddress, destinationNetwork}: UpdateRefundAddressParams) => {
      // When clearing (refundAddress is null), send empty string to clear on backend
      // When setting, send the actual address
      const refundParams = {
        refund_data: {
          refund_address: refundAddress ?? '',
          destination_network: destinationNetwork,
        },
      };

      const result = await updatePayinSessionMutation({
        variables: {
          input: {
            client_secret: clientSecret,
            crypto_payin_session: sessionId,
            refund_params: refundParams,
          },
        },
      });

      if (result.error) {
        throw result.error;
      }

      if (!result.data?.v1CryptoInternalPayinSessionUpdate) {
        throw new Error('Failed to update refund address');
      }
    },
    [sessionId, clientSecret, updatePayinSessionMutation],
  );

  return [updateRefundAddress, {loading}];
};
